/**
 * Barre d'onglets (ARCHITECTURE.md §3 `components/ui/TabBar`, §6.3).
 *
 * 🔴 **Barre pleine ancrée au bas de l'écran, depuis la refonte du
 * 25/08/2026.** C'était une capsule flottante translucide, dans l'esprit des
 * barres d'iOS 26 (specs §8.1) ; c'est précisément ce qui a été jugé raté à
 * l'usage — « on oublie la tentative de copie de liquid glass ». Le matériau
 * est décrit dans `styles/surfaces.css`.
 *
 * Deux choix méritent d'être justifiés, parce qu'ils se défont facilement :
 *
 * 1. **La barre ne défile pas** : elle est hors du flux, toujours visible, et
 *    `.app-main` réserve sa hauteur (`--tabbar-space`) — le contenu s'arrête
 *    donc au-dessus, **rien n'est jamais inatteignable**. Elle n'a en revanche
 *    plus besoin que le contenu passe *derrière* elle : ce n'était nécessaire
 *    que pour donner au flou quelque chose à flouter.
 *
 * 2. **Le libellé reste**, sous l'icône. Une icône seule est une devinette —
 *    « Bibliothèque » ne se lit pas dans une pile de livres — et l'onglet actif
 *    ne se signalerait plus que par la couleur. Les glyphes sont `aria-hidden`
 *    et c'est le texte qui porte l'information. ⚠️ L'état actif est d'ailleurs
 *    doublé d'un trait au-dessus du glyphe **et** d'`aria-current="page"` : la
 *    couleur n'est jamais le seul signal.
 */

import type { ComponentType } from 'react';
import { Link, useLocation } from 'react-router';

import { targetFor, type TabRoot } from '../../lib/tabMemory';
import { IconLibrary, IconProgress, IconSessions, IconToday } from './icons';

interface Tab {
  to: string;
  /** Racine de l'onglet dans l'URL — la clé de la mémoire de navigation. */
  root: TabRoot;
  label: string;
  Icon: ComponentType;
  /**
   * Préfixe d'URL qui doit **aussi** allumer cet onglet.
   *
   * ⚠️ `/jour/:date` est le même écran que l'onglet Aujourd'hui, à une autre
   * date, et doit donc l'allumer lui aussi.
   */
  alsoMatches?: string;
}

/**
 * 🔴 **QUATRE onglets, et Réglages n'en est plus un** *(29/08/2026, après usage
 * sur l'appareil)*.
 *
 * Les specs §8.2 en faisaient un onglet, et la V1 l'a servi ainsi. Ce qui a
 * changé, c'est le nombre de voisins : à cinq, la rangée devenait serrée et
 * **trois libellés sur cinq étaient tronqués**. Or Réglages est de très loin
 * l'onglet le moins ouvert — il ne contient que du réglage, par définition
 * rare. Lui laisser un cinquième de la barre revenait à faire payer les quatre
 * gestes quotidiens pour un geste mensuel. Il vit désormais en haut à droite de
 * l'en-tête (`ScreenHeader`), place conventionnelle d'un accès secondaire.
 *
 * 🔴 **Et le problème de largeur disparaît avec lui.** La boîte d'un libellé
 * passe de 63 px à **80,7 px** (393) et de 59 à **76,2** (375) : « Musculation »
 * (~72), « Bibliothèque » (72,9) et « Progression » (68,4) y tiennent tous. Le
 * 5ᵉ onglet, qui avait dû s'appeler « Séances » puis « Muscu » faute de place,
 * **reprend donc son nom entier** — et il est enfin celui de l'écran.
 *
 * ⚠️ Ce n'était pas une conclusion évidente : trois tours de renommage ont
 * cherché le mot qui rentrait, alors que ce qui ne rentrait pas était **le
 * nombre d'onglets**. Retirer le moins utile a réglé les trois libellés d'un
 * coup, là où aucun choix de vocabulaire n'y parvenait.
 *
 * 🔴 **L'ordre :** Journal · Bibliothèque · Musculation · Progression. Les trois
 * onglets de la V1 gardent exactement leur rang, et le nouveau s'insère en 3ᵉ.
 * Le §8.2 des specs énumère un contenu, il ne prescrit pas un ordre — même
 * arbitrage qu'aux sous-onglets de la Bibliothèque.
 */
const TABS: Tab[] = [
  // ⚠️ « Journal » et non « Aujourd'hui » : l'onglet sert aussi à consulter et
  // corriger les jours passés, `alsoMatches` en témoigne. Le dossier reste
  // `screens/today/`, nommé par l'arborescence du §3.
  { to: '/', root: '', label: 'Journal', Icon: IconToday, alsoMatches: '/jour/' },
  { to: '/bibliotheque', root: 'bibliotheque', label: 'Bibliothèque', Icon: IconLibrary },
  { to: '/musculation', root: 'musculation', label: 'Musculation', Icon: IconSessions },
  { to: '/progression', root: 'progression', label: 'Progression', Icon: IconProgress },
];

/**
 * Correspondance **exacte**, plus les préfixes déclarés.
 *
 * ⚠️ Écrite à la main plutôt que déléguée à `NavLink`, et ce n'est pas une
 * préférence de style — `NavLink` ne pouvait pas faire ce travail :
 *   - `to="/" end` ne s'active que sur `/` **exactement**, donc changer de jour
 *     éteignait les quatre onglets à la fois (défaut constaté au navigateur) ;
 *   - retirer `end` ne corrige rien : `to="/"` correspondrait alors à *toutes*
 *     les routes et l'onglet resterait allumé jusque dans les Réglages ;
 *   - et forcer l'attribut à la main ne marche pas non plus, car `NavLink`
 *     **consomme** l'`aria-current` qu'on lui passe — il le lit comme « quelle
 *     valeur poser quand je serai actif », puis l'ignore quand il ne l'est pas.
 */
function isTabActive(tab: Tab, pathname: string): boolean {
  if (pathname === tab.to) return true;
  // Sous-page : `/bibliotheque/aliments`, `/reglages/notifications`.
  if (tab.to !== '/' && pathname.startsWith(`${tab.to}/`)) return true;
  return tab.alsoMatches !== undefined && pathname.startsWith(tab.alsoMatches);
}

export function TabBar() {
  const { pathname } = useLocation();

  return (
    <nav className="app-tabbar" aria-label="Sections">
      {TABS.map((tab) => {
        const active = isTabActive(tab, pathname);
        const Icon = tab.Icon;
        /*
         * 🔴 **La destination dépend d'où l'on se trouve** (demandé après usage).
         * Depuis un autre onglet, on revient là où on avait laissé celui-ci —
         * le 14 août dans le journal, la section « Recettes » de la
         * bibliothèque. Depuis l'onglet lui-même, on revient à sa page par
         * défaut : c'est ce qui rend le second appui utile plutôt que sans
         * effet, et c'est ce que font les barres d'onglets d'iOS.
         *
         * ⚠️ Calculée dans le `to` du lien, **pas dans un `onClick`** qui
         * appellerait `navigate()`. Le lien garde ainsi une vraie `href` : le
         * navigateur affiche la destination, l'ouverture dans un nouvel onglet
         * fonctionne, et surtout l'historique reste piloté par lui — donc le
         * retour par glissement natif du §6.1 aussi.
         */
        const to = targetFor(tab.root, tab.to, active);

        return (
          <Link
            key={tab.to}
            to={to}
            className={active ? 'app-tab app-tab--active' : 'app-tab'}
            aria-current={active ? 'page' : undefined}
          >
            <Icon />
            <span className="app-tab__label">{tab.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
